import { ChangeDetectorRef, Component, ElementRef, KeyValueDiffers, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { Observable, Subscription, filter, tap, debounce, of, timer } from 'rxjs';

import { BlockComponent } from '../Block/block.component';
import { TextDisplayElementModel } from '../../models/text-display-element.model';
import { RichTextDisplayElementModel } from '../../models/rich-text-display-element.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { MonitoringTextStyleModel } from '../../../../Monitor/Shared/models/monitoring-text-style.model';
import { RichTextEditorDefaultStylesModel } from '../../../Forms/models/rich-text-editor-default-styles.model';
import { AbstractPageBuilderService } from '../../services/abstract-page-builder.service';
import { DomSanitizer } from '@angular/platform-browser';
import { EmailHelper } from '../../helpers/email.helper';
import { ClickOutsideService } from '../../services/click-outside.service';
import { BlockValidationTypeEnum } from '../../enums/block-validation-type.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { DistributionBlockTypeEnum } from '../../../../Discover/Targeting/enums/distribution-block-type.enum';
import { BriefingBlockTypeEnum } from '../../../../Monitor/Briefings/enums/briefing-block-type.enum';
import { BlockTypeModel } from '../../../PageBuilder/models/block-type.model';
import { RichTextEditorConfigModel } from '../../../Forms/components/RichTextEditorCK/model/rich-text-editor-config.model';
import { BlockDisplayElementChangedEvent } from '../../models/block-display-element-changed-event.model';

@Component({
    selector: 'agpr-rich-text-block',
    templateUrl: './rich-text-block.component.html',
    styleUrls: ['./rich-text-block.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RichTextBlockComponent extends BlockComponent implements OnInit, OnDestroy {
    readonly briefingBlockTypeEnum = BriefingBlockTypeEnum;

    content: string = '';
    buttonTypesEnum = ButtonTypesEnum;
    defaultEditorStyles: RichTextEditorDefaultStylesModel;
    richTextEditorConfig: RichTextEditorConfigModel;
    enableAIHelper: boolean = false;
    enableAIRefinement: boolean = false;
    enablePersonalizations: boolean;
    enableLineHeight: boolean;
    enableFontColor: boolean;
    enableBackgroundColor: boolean;
    enableHtmlEditor: boolean;
    enableFontFamily: boolean;
    enableFontSize: boolean;
    enableInsertImage: boolean;
    enableStrikethrough: boolean;
    enableNumberedList: boolean;
    enableBlockQuote: boolean;
    aiReferenceId: string;
    allowImage: boolean;
    pastePlainText: boolean;
    mergeTagOptions: DropdownOptionModel[];
    blocks: BlockModel[] = [];
    textBlockType: BlockTypeModel;
    isFooter: boolean;
    isPartOfMuliColumn: boolean = false;

    subscriptions: Subscription[] = [];

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        protected changeDetectorRef: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
        public clickOutsideService: ClickOutsideService,
        public el: ElementRef
    ) {
        super(differs, pageBuilderService, changeDetectorRef, clickOutsideService, el);
    }

    ngOnInit() {
        this.clickOutsideService.setClassComparisonRegex(this.block.clickOutsideComparisonRegex);

        this.displayDiffer = this.differs.find(this.block.displayElement).create();
        this.stylesDiffer = this.differs.find(this.block.displayElement.styles).create();
        super.assignId();
        this.areLayoutBlocksAllowed = this.pageBuilderService.areLayoutBlocksAllowed();
        if (this.isInLayoutBlock) {
            this.setContextMenuInLayoutBlockMovingOptions();
            this.setContextMenuEditingOptions();
        }

        if (this.isInCuratorBlock) {
            this.setContextMenuInCuratorBlockMovingOptions();
            this.setContextMenuEditingOptions();
        }

        this.setContextMenuMovingOptions();

        this.subscriptions.push(
            this.pageBuilderService.getSelectedBlock().subscribe(
                (block) => {
                    const currentBlockIsSelected = this.block === block;

                    if (currentBlockIsSelected && ((<TextDisplayElementModel>block.displayElement)?.text)) {
                        this.content = (<TextDisplayElementModel>block.displayElement)?.text;
                    }

                    if (this.editing && !currentBlockIsSelected) {
                        this.applyFormatingForEmail();
                    }

                    this.setEditing(currentBlockIsSelected);
                }
            ),
            this.pageBuilderService.getBlocks().subscribe(
                (blocks) => {
                    this.blocks = blocks;
                    // Only rebuild context menus if the block's moveability state has changed.
                    if (this.hasMoveabilityChanged()) {
                        if (this.isInLayoutBlock) {
                            this.setContextMenuInLayoutBlockMovingOptions();
                            this.setContextMenuEditingOptions();
                        }

                        if (this.isInCuratorBlock) {
                            this.setContextMenuInCuratorBlockMovingOptions();
                            this.setContextMenuEditingOptions();
                        }

                        this.setContextMenuMovingOptions();
                        this.updateCachedMoveState();
                    }
                }
            ),
            this.pageBuilderService.getBlockTypes().pipe(filter(x => !!x)).subscribe(
                (blockTypes) => {
                    this.textBlockType = blockTypes.find(blockType => {
                        return (blockType.key === DistributionBlockTypeEnum.text);
                    });
                    if (!this.textBlockType) {
                        this.textBlockType = blockTypes.find(blockType => {
                            return (blockType.key === BriefingBlockTypeEnum.TEXT);
                        });
                    }
                }
            ),
            this.clickOutsideService.getClickOutsideBlock().subscribe((deselectionEvent: boolean) => {
                if (deselectionEvent && this.editing) {
                    if (this.editing && !this.allowImage) {
                        this.pageBuilderService.validateBlock(this.block, [BlockValidationTypeEnum.noImageInTextBlock]);
                    }
                    // applyFormatingForEmail must run before setEditing(false) so that the CK editor
                    // source-editing DOM element is still present when we read its data-value attribute.
                    this.applyFormatingForEmail();
                    this.pageBuilderService.deselectBlock(true);
                }
            }),
            this.subscribeToBlockChanges().subscribe(),
            this.pageBuilderService.getBlockTypeGroups().subscribe(blockTypeGroups => {
                this.blockTypeGroups = blockTypeGroups;
                if (this.isInLayoutBlock || this.isInCuratorBlock) {
                    this.setContextMenuEditingOptions();
                }
            }),
            this.pageBuilderService.getFocusedBlockInfo().pipe(filter(x => !!x)).subscribe(moveInfo => {
                this.moveInfo = moveInfo;
                this.restoreFocus(this.moveInfo);
            }),
            // do not remove, we need it for inline adder position (ngInit overrides the block component ngInit)
            this.pageBuilderService.getInlineAdderHeight().subscribe(height => {
                this.inlineAdderHeight = height;
            })
        );

        this.initializeContent((<TextDisplayElementModel>this.block.displayElement)?.text);

        // Bring this back in for CK-editor
        this.enablePersonalizations = (<RichTextDisplayElementModel>this.block.displayElement)?.enablePersonalizations;
        this.mergeTagOptions = (<RichTextDisplayElementModel>this.block.displayElement)?.mergeTagOptions;
        this.enableLineHeight = (<RichTextDisplayElementModel>this.block.displayElement)?.enableLineHeight;
        this.enableFontColor = (<RichTextDisplayElementModel>this.block.displayElement)?.enableFontColor;
        this.enableBackgroundColor = (<RichTextDisplayElementModel>this.block.displayElement)?.enableBackgroundColor;
        this.enableFontFamily = (<RichTextDisplayElementModel>this.block.displayElement)?.enableFontFamily;
        this.enableFontSize = (<RichTextDisplayElementModel>this.block.displayElement)?.enableFontSize;
        this.enableInsertImage = (<RichTextDisplayElementModel>this.block.displayElement)?.enableInsertImage;
        this.enableStrikethrough = (<RichTextDisplayElementModel>this.block.displayElement)?.enableStrikethrough;
        this.enableNumberedList = (<RichTextDisplayElementModel>this.block.displayElement)?.enableNumberedList;
        this.enableBlockQuote = (<RichTextDisplayElementModel>this.block.displayElement)?.enableBlockQuote;
        this.enableHtmlEditor = (<RichTextDisplayElementModel>this.block.displayElement)?.enableHtmlEditor;
        this.enableAIHelper = (<RichTextDisplayElementModel>this.block.displayElement)?.enableAIHelper;
        this.enableAIRefinement = this.enableAIHelper || (<RichTextDisplayElementModel>this.block.displayElement)?.enableAIRefinement;
        this.allowImage = (<RichTextDisplayElementModel>this.block.displayElement)?.allowImage;
        this.pastePlainText = (<RichTextDisplayElementModel>this.block.displayElement)?.pastePlainText;

        this.richTextEditorConfig = new RichTextEditorConfigModel({
            enablePersonalizations: (<RichTextDisplayElementModel>this.block.displayElement)?.enablePersonalizations,
            enableLineHeight: (<RichTextDisplayElementModel>this.block.displayElement)?.enableLineHeight,
            enableHeading: (<RichTextDisplayElementModel>this.block.displayElement)?.enableHeading,
            enableFontColor: (<RichTextDisplayElementModel>this.block.displayElement)?.enableFontColor,
            enableBackgroundColor: (<RichTextDisplayElementModel>this.block.displayElement)?.enableBackgroundColor,
            enableHtmlEditor: (<RichTextDisplayElementModel>this.block.displayElement)?.enableHtmlEditor,
            enableAIHelper: (<RichTextDisplayElementModel>this.block.displayElement)?.enableAIHelper,
            enableAIRefinement: this.enableAIHelper || (<RichTextDisplayElementModel>this.block.displayElement)?.enableAIRefinement,
            enableFontFamily: (<RichTextDisplayElementModel>this.block.displayElement)?.enableFontFamily,
            enableFontSize: (<RichTextDisplayElementModel>this.block.displayElement)?.enableFontSize,
            enableInsertImage: (<RichTextDisplayElementModel>this.block.displayElement)?.enableInsertImage,
            enableStrikethrough: (<RichTextDisplayElementModel>this.block.displayElement)?.enableStrikethrough,
            enableNumberedList: (<RichTextDisplayElementModel>this.block.displayElement)?.enableNumberedList,
            enableAlignment: (<RichTextDisplayElementModel>this.block.displayElement)?.enableAlignment,
            enableBlockQuote: (<RichTextDisplayElementModel>this.block.displayElement)?.enableBlockQuote,
            enableDirection: (<RichTextDisplayElementModel>this.block.displayElement)?.enableDirection,
            enableIndent: (<RichTextDisplayElementModel>this.block.displayElement)?.enableIndent,
            enableTranslation: (<RichTextDisplayElementModel>this.block.displayElement)?.enableTranslation,
            pastePlainText: (<RichTextDisplayElementModel>this.block.displayElement)?.pastePlainText,
            aiReferenceId: (<RichTextDisplayElementModel>this.block.displayElement)?.aiReferenceId
        });

        this.mergeTagOptions = (<RichTextDisplayElementModel>this.block.displayElement)?.mergeTagOptions;
        this.aiReferenceId = (<RichTextDisplayElementModel>this.block.displayElement)?.aiReferenceId;
        this.isFooter = this.block.type.key === DistributionBlockTypeEnum.footer;

        // _blockPosition is x,y,z which why is the column index.. so if isRightMost & index zero, that means it's a column layout 1, else it's 2 or 3
        this.isPartOfMuliColumn = (this.isInLayoutBlock || this.isInCuratorBlock) && !(Number(this._blockPosition.split('.')?.[1]) === 0 && this.isRightMost);

        this.updateDefaultStyles();
        this.setBlockAdderOptions();
        this.ngOnChanges();
        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((value) => {
            value.unsubscribe();
        });
    }

    /**
     * Sets block content
     */
    initializeContent(content: string) {
        if (content === undefined || content === null) {
            return;
        }

        if (!this.editing) {
            this.content = content;
        } else {
            this.content = EmailHelper.formatLineHeightForRTE(content);
            if (this.content) {
                (<TextDisplayElementModel>this.block.displayElement).text = this.content;
            }
        }
    }

    subscribeToBlockChanges(): Observable<BlockDisplayElementChangedEvent> {
        return this.pageBuilderService.getBlockDisplayElementChanged().pipe(
            debounce((event) => event.block === this.block ? of(null) : timer(300)),
            tap(event => {
                // If event.block is null, all blocks should update
                // If event.block matches this block, this block should update
                if (!event.block || event.block === this.block) {
                    // Handle style-specific updates
                    this.updateDefaultStyles();
                    this.renderable = this.block.displayElement.canRender();
                    this.updateTwigData();
                    this.changeDetectorRef.detectChanges();
                }
            })
        );
    }

    /**
     * Update default styles
     */
    updateDefaultStyles() {
        const textDisplayElement = <TextDisplayElementModel>this.block.displayElement;
        const styles = <MonitoringTextStyleModel>textDisplayElement.styles;

        this.defaultEditorStyles = styles.toTextEditorStyles();
    }

    /**
     * Returns True if text contains HTML
     */
    checkIfTextContainsHTML(text): boolean {
        return /<[a-z][\s\S]*>/i.test(text);
    }

    /**
     * Handles changes to the text content
     */
    changes() {
        if ((<TextDisplayElementModel>this.block.displayElement).htmlEdit) {
            this.content = this.xssSanitize(this.content);
        }

        (<TextDisplayElementModel>this.block.displayElement).text = this.content;
        this.block.errors = [];

        if (!(<TextDisplayElementModel>this.block.displayElement).editorDirty) {
            (<TextDisplayElementModel>this.block.displayElement).editorDirty = true;
            this.pageBuilderService.formFieldUpdated();
        }
        this.changeDetectorRef.detectChanges();
    }

    /**
     * sanitizing the string content by stripping out <script> and <xss> tags
     * @param content original string content
     * @returns content without <script> and <xss> tags
     */
    xssSanitize(content: string): string {
        const SCRIPT_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
        const XSS_REGEX = /<xss\b[^<]*(?:(?!<\/xss>)<[^<]*)*<\/xss>/gi;

        return content.replace(SCRIPT_REGEX, '').replace(XSS_REGEX, '');
    }

    /**
     * Apply formatting for email
     */
    applyFormatingForEmail(): void {
        const htmlContent = document.querySelector('.ck-source-editing-area')?.getAttribute('data-value');
        if (htmlContent) {
            (<TextDisplayElementModel>this.block.displayElement).text = htmlContent;
        }
        if (!(<TextDisplayElementModel>this.block.displayElement).htmlEdit) {
            this.content = EmailHelper.getTextContentStyledForEmail((<TextDisplayElementModel>this.block.displayElement).text);
        }

        (<TextDisplayElementModel>this.block.displayElement).text = this.content;

        this.setEditing(false);
        this.pageBuilderService.formFieldUpdated();
    }

    /**
     * Sets the block's editing state
     */
    setEditing(editing: boolean): void {
        if (this.editing === editing) {
            return;
        }
        this.editing = editing;

        this.initializeContent(this.content);

        this.pageBuilderService.blockEditingStateChanged(this.block, this.editing, this.blockPosition);
        if (this.editing) {
            this.clickOutsideService.setClassComparisonRegex(this.block.clickOutsideComparisonRegex);
        } else {
            this.clickOutsideService.removeClassComparisonRegex();
        }
        this.changeDetectorRef.detectChanges();
    }

    insertTextBlock(html: string): void {
        this.pageBuilderService.formFieldUpdated();

        const displayElementModel: RichTextDisplayElementModel = (this.block.displayElement.clone() as RichTextDisplayElementModel);
        displayElementModel.text = html;
        displayElementModel.isBoundToGlobalStyles = this.block.displayElement.isBoundToGlobalStyles;

        const block = new BlockModel(
            null,
            this.textBlockType,
            displayElementModel
        );

        this.pageBuilderService.addBlockAtPosition(block, this.blockPositionBottom);
        // TODO: need to select the block
        this.pageBuilderService.selectBlock(block);
    }
}
